import { useEffect, useState } from "react";
import { ApiError, type MiloStatus } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";
import { miloPositionLine, tierChangeNote, tierLabel, topUpLine } from "./miloTiers";

/**
 * Milo on the household's plan (KEY-716), in Your settings, beside the Subscription card: which rung of the ladder
 * is held — 300, 450, 600 or 900 requests a month on the catalogue's rungs — where the pool stands this month, and
 * a way to move up, move down or take Milo off. Tim (1 Oct 2026): "users should be able to upgrade their Milo
 * subscriptions as well from their instance".
 *
 * The ladder comes from the server with the pool (`miloStatus`, no dial in view = the account's own pool), so the
 * card offers exactly what the plan sells. Changing rung goes through the same add-on service the admin app uses:
 * up is charged now, pro rata; down applies next period. Shown only where Milo exists at all (the environment flag
 * and billing both on); a plan that sells no Milo shows nothing to pick.
 */
export function MiloCard({ organisationId }: { organisationId: string }) {
  const [status, setStatus] = useState<MiloStatus | null | undefined>(undefined);
  const [chosen, setChosen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);

  const refresh = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const next = await api.miloStatus(token);
      setStatus(next);
      setChosen(next.tierRequests ?? 0);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  // Nothing to say until it answers; nothing to say where Milo does not exist or billing is off (no ladder).
  if (status === undefined) return null;
  if (!status || !status.enabled || !status.tiers || status.tiers.length === 0) return null;

  const current = status.tierRequests ?? 0;
  // The ladder's prices are in the plan's own period, which the status says (packAnnual).
  const annual = status.packAnnual === true;
  const position = miloPositionLine(status);
  const change = chosen === null ? null : tierChangeNote(current, chosen);

  // KEY-717: 300 more, charged to the card on file now, spent after the month's own and rolling over.
  const buyTopUp = async () => {
    const token = getAccessToken();
    if (!token || buying) return;
    setBuying(true);
    setNote(null);
    try {
      const receipt = await api.buyOwnMiloTopUp(token, organisationId);
      await refresh();
      setNote(receipt.granted
        ? `${receipt.requests} more requests added.${receipt.note ? ` ${receipt.note}` : ""}`
        : receipt.note ?? "The payment is being taken; the requests arrive when it clears.");
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "Couldn't buy a top-up just now.");
    } finally {
      setBuying(false);
    }
  };

  const save = async () => {
    const token = getAccessToken();
    if (!token || chosen === null || chosen === current) return;
    setBusy(true);
    setNote(null);
    try {
      await api.setOwnMiloTier(token, organisationId, chosen);
      await refresh();
      setNote(chosen === 0 ? "Milo Plus will come off your plan at the end of this period — free Milo stays." : `Milo Plus is on your plan at ${chosen} requests a month.`);
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "Couldn't change Milo Plus just now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsCard
      title="Milo Plus"
      hint="Free Milo handles the everyday things. Milo Plus understands anything in your own words and plans from letters and photos — one pool of requests a month, shared by everyone on your dials. You can change it any time."
    >
      {position && <div style={{ fontSize: 13.5, color: "var(--text)" }}>{position}{topUpLine(status) ? ` ${topUpLine(status)}` : ""}</div>}
      <div role="radiogroup" aria-label="Milo Plus tier" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {status.tiers.map((tier) => (
          <label key={tier.requests} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: "var(--text)", cursor: busy ? "wait" : "pointer" }}>
            <input
              type="radio"
              name="milo-tier"
              checked={chosen === tier.requests}
              disabled={busy}
              onChange={() => setChosen(tier.requests)}
            />
            <span>{tierLabel(tier, annual)}</span>
            {tier.requests === current && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>· yours now</span>}
          </label>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: "var(--text)", cursor: busy ? "wait" : "pointer" }}>
          <input type="radio" name="milo-tier" checked={chosen === 0} disabled={busy} onChange={() => setChosen(0)} />
          <span>Free Milo only</span>
          {current === 0 && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>· yours now</span>}
        </label>
      </div>
      {change && <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)" }}>{change}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className="kd-btn kd-btn-sm" onClick={() => void save()} disabled={busy || buying || chosen === null || chosen === current}>
          {busy ? "Saving…" : chosen === 0 ? "Remove Milo Plus" : current === 0 ? "Add Milo Plus" : "Change tier"}
        </button>
        {current > 0 && status.topUpPence != null && (
          <button
            type="button"
            className="kd-btn kd-btn-sm"
            onClick={() => void buyTopUp()}
            disabled={busy || buying}
            title="300 more requests, charged now. They're used after this month's and carry over."
          >
            {buying ? "Buying…" : `Buy 300 more for £${(status.topUpPence / 100).toFixed(2)}`}
          </button>
        )}
        {note && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{note}</span>}
      </div>
    </SettingsCard>
  );
}
